import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ShieldCheck, Users, LifeBuoy, Send, ArrowRight, CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";

const StatusPill = ({ active, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${active ? "bg-brand-green/10 text-brand-green" : "bg-slate-500/10 text-slate-400"}`}>
    {active ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />} {label}
  </span>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  useSEO("Dashboard");
  const [vip, setVip] = useState(null);
  const [verifs, setVerifs] = useState([]);
  const [refs, setRefs] = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/vip/status").then((r) => setVip(r.data)).catch(() => {});
    api.get("/broker-verifications").then((r) => setVerifs(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setRefs(r.data)).catch(() => {});
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
  }, []);

  const vipActive = vip?.vip?.active;
  const cards = [
    { to: "/dashboard/vip-access", icon: Crown, title: t("vip_status"), value: vipActive ? "Active" : "Inactive", active: vipActive },
    { to: "/dashboard/broker-verification", icon: ShieldCheck, title: t("broker_verification"), value: `${verifs.length} submitted` },
    { to: "/dashboard/referrals", icon: Users, title: t("referrals"), value: refs ? `${refs.count} referred` : "—" },
    { to: "/dashboard/tickets", icon: LifeBuoy, title: t("support"), value: `${tickets.length} tickets` },
  ];

  return (
    <div className="space-y-6">
      <div className="card-surface rounded-2xl p-7 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-[0.06]" />
        <div className="relative">
          <p className="font-mono text-xs uppercase tracking-widest text-brand-cyan">{t("my_dashboard")}</p>
          <h1 className="font-display mt-2 text-2xl sm:text-3xl font-bold text-white">Welcome, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="mt-2 text-slate-400 text-sm">Manage your VIP access, broker verification, referrals and support here.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="card-surface hover-glow rounded-2xl p-5 group" data-testid={`dash-card-${c.title}`}>
            <div className="flex items-center justify-between">
              <div className="h-10 w-10 rounded-lg bg-brand-cyan/10 flex items-center justify-center"><c.icon className="h-5 w-5 text-brand-cyan" /></div>
              <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-brand-cyan transition-colors rtl-mirror" />
            </div>
            <p className="mt-4 text-sm text-slate-400">{c.title}</p>
            <p className="mt-1 font-display font-semibold text-white">{c.value}</p>
          </Link>
        ))}
      </div>

      {/* Telegram VIP access */}
      <div className="card-surface rounded-2xl p-7">
        <div className="flex items-center gap-3 mb-2">
          <Send className="h-5 w-5 text-brand-cyan" />
          <h2 className="font-display font-semibold text-white">{t("telegram_access")}</h2>
          <StatusPill active={vipActive} label={vipActive ? "Eligible" : "Not eligible"} />
        </div>
        {vipActive && vip?.telegram_vip_link ? (
          <a href={vip.telegram_vip_link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full gradient-brand text-brand-navy font-semibold px-6 py-2.5 text-sm" data-testid="telegram-vip-link">
            <Send className="h-4 w-4" /> Join VIP Telegram Channel
          </a>
        ) : vipActive ? (
          <p className="text-sm text-slate-400 mt-2">Your VIP is active. The Telegram channel link will appear here once configured by admin.</p>
        ) : (
          <p className="text-sm text-slate-400 mt-2">Activate VIP to receive your private INVIXA Telegram VIP channel access. <Link to="/dashboard/vip-access" className="text-brand-cyan hover:underline">Get VIP →</Link></p>
        )}
      </div>

      <div className="card-surface rounded-2xl p-5 text-xs text-slate-500">
        By using INVIXA you agree to our <Link to="/legal/terms" className="text-brand-cyan">Terms</Link>, <Link to="/legal/privacy" className="text-brand-cyan">Privacy Policy</Link> and <Link to="/legal/risk" className="text-brand-cyan">Risk Disclosure</Link>.
      </div>
    </div>
  );
}
